import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../utils/api';

const validatePassword = (value) => {
  const rules = {
    length: value.length >= 8,
    lowercase: /[a-z]/.test(value),
    uppercase: /[A-Z]/.test(value),
    number: /\d/.test(value),
    symbol: /[^A-Za-z\d]/.test(value),
  };

  return {
    isValid: Object.values(rules).every(Boolean),
    rules,
  };
};

function ResetPassword() {
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordRules, setPasswordRules] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    symbol: false,
  });

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordRules(validatePassword(value).rules);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setMessage('');

    if (!email || !token) {
      setError('This reset link is incomplete. Please request a new one.');
      return;
    }

    const validation = validatePassword(password);

    if (!validation.isValid) {
      setError('Password must be at least 8 characters and include uppercase, lowercase, number, and special character.');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword({
        email,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });

      if (response.status === false || response.success === false) {
        setError(response.message || 'Password reset failed.');
        return;
      }

      setMessage(response.message || 'Password reset successfully.');
      setPassword('');
      setPasswordConfirmation('');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Password reset failed.');
    } finally {
      setLoading(false);
    }
  };

  const Rule = ({ passed, children }) => (
    <p className={passed ? 'text-green-600' : 'text-red-500'}>
      {passed ? '✓' : '•'} {children}
    </p>
  );

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl border border-gray-100 p-8">
        <h1 className="text-3xl font-playfair-display font-semibold text-primary mb-3">
          Reset Password
        </h1>

        <p className="text-secondary mb-6">
          Create a new secure password for your account.
        </p>

        {error && <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-xl">{error}</div>}
        {message && <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-xl">{message}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <input
              type="password"
              required
              value={password}
              onChange={handlePasswordChange}
              placeholder="New password"
              className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            />

            <div className="text-sm mt-3 space-y-1">
              <Rule passed={passwordRules.length}>At least 8 characters</Rule>
              <Rule passed={passwordRules.lowercase}>Contains lowercase letter</Rule>
              <Rule passed={passwordRules.uppercase}>Contains uppercase letter</Rule>
              <Rule passed={passwordRules.number}>Contains a number</Rule>
              <Rule passed={passwordRules.symbol}>Contains a special character</Rule>
            </div>
          </div>

          <input
            type="password"
            required
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            placeholder="Confirm new password"
            className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />

          {passwordConfirmation && password !== passwordConfirmation && (
            <p className="text-sm text-red-500">Passwords do not match.</p>
          )}

          {passwordConfirmation && password === passwordConfirmation && (
            <p className="text-sm text-green-600">Passwords match.</p>
          )}

          <button
            type="submit"
            disabled={loading || !!message}
            className="w-full bg-primary text-cream py-4 rounded-full font-medium disabled:opacity-60"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        {message && (
          <p className="mt-6 text-center">
            <Link to="/login" className="text-primary font-medium">
              Go to Login
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;